import type {WebSocket} from 'ws';
import type {Relays} from '../../types.ts';
import type {ServerWebSocketHandler} from '@interopio/gateway-server/web/server';
import {IOGateway} from '@interopio/gateway';

function onMessage(relays: Relays,
                   log: IOGateway.Logging.Logger,
                   key: string,
                   node: string | undefined,
                   socketsByNodeId: Map<string, Map<string, WebSocket>>,
                   msg: string) {
    try {
        relays.send(key, node, msg, (k, err?: Error) => {
            if (err) {
                log.warn(`${k} error writing msg ${msg}: ${err}`);
                return;
            }
            if (log.enabledFor('debug')) {
                log.debug(`${k} sent msg ${msg}`);
            }
        });
    } catch (ex) {
        log.error(`${key} unable to process message`, ex);
        if (node) {
            const socket = socketsByNodeId.get(node)?.get(key);
            socket?.terminate();
        }
    }
}
let handlerId = 0;
async function create(
    log: IOGateway.Logging.Logger,
    relays: Relays,
    env: { endpoint: string }
): Promise<ServerWebSocketHandler> {
    const socketsByNodeId = new Map<string, Map<string, WebSocket>>();
    relays.onMsg = (k, nodeId, msg) => {
        try {
            const sockets = socketsByNodeId.get(nodeId);
            if (sockets && sockets.size > 0) {
                for (const [key, socket] of sockets) {
                    socket.send(msg, {binary: false}, (err?: Error) => {
                        if (err) {
                            log.warn(`${key} error writing from ${k} msg ${msg}: ${err}`);
                            return;
                        }
                        if (log.enabledFor('debug')) {
                            log.debug(`${key} sent from ${k} msg ${msg}`);
                        }
                    });
                }
            } else {
                log.warn(`${k} dropped msg ${msg}.`);
            }
        } catch (ex) {
            log.error(`${k} unable to process message`, ex);
        }
    };

    const id = ++handlerId;
    let keyId = 0;

    return async ({socket, handshake}) => {
        const logPrefix = handshake.logPrefix ?? '';
        const query = handshake.url.searchParams;
        const key = `c.${id}.${++keyId}`;
        log.info(`${logPrefix}connected on /cluster with ${query} with assigned key ${key}`);

        const node = query.get('node');
        if (node) {
            let sockets = socketsByNodeId.get(node);
            if (!sockets) {
                sockets = new Map<string, WebSocket>();
                socketsByNodeId.set(node, sockets);
            }
            sockets.set(key, socket);
        } else {
            socket.terminate();
            return;
        }
        //onOpen(connectedNodes, key);
        socket.on('error', (err: Error) => {
            log.error(`${key} websocket error: ${err}`, err);
        });
        socket.on('message', (data, _isBinary) => {
            if (Array.isArray(data)) {
                data = Buffer.concat(data);
            }
            onMessage(relays, log, key, node, socketsByNodeId, data as unknown as string);
        });
        socket.on('close', (_code, _reason) => {
            log.info(`${key} disconnected from cluster`);
            const sockets = socketsByNodeId.get(node);
            if (sockets) {
                sockets.delete(key);
                if (sockets.size === 0) {
                    socketsByNodeId.delete(node);
                }
            }
        });
    };
}

export const meshCluster = ({logger, relays}: { logger: IOGateway.Logging.Logger, relays: Relays }) => {
    return async (environment: { endpoint: string }) => {
        return await create(logger, relays, environment);
    }
};
